import { Dialog, type DialogProps } from "@cmsgov/design-system-12";
import {
  type ForwardRefRenderFunction,
  Fragment,
  useEffect,
  useId,
  useMemo,
  useRef,
  type ReactNode,
  forwardRef,
  useCallback,
} from "react";
import { devDebug } from "../helpers/loggingHelpers";
import { FocusTrap, type FocusTrapProps } from "focus-trap-react";
import classNames from "classnames";
import { TESTING } from "../config";
import { PortalWrapper } from "./PortalWrapper";
import type { FCWithChildren } from "../@types";
import { tabbable } from "tabbable";
import {
  FOCUS_TRAP_CHECK_INTERVAL,
  FOCUS_TRAP_TIMEOUT_FALLBACK,
} from "../helpers/constants/focusTrap";

export type { DialogProps } from "@cmsgov/design-system-12";
interface MctDialogProps {
  hasFocusTrap?: boolean;
  /**
   * If set to `true`, actions (CTA buttons), will stack and stretch to 100% at narrower viewport widths
   */
  responsiveActions?: boolean;
}

const DialogFocusTrapContainerComponent: ForwardRefRenderFunction<
  HTMLDivElement,
  {
    children: ReactNode;
  }
> = ({ children }, containerRef) => (
  <div className="mct-c-dialog__focus-trap-container" ref={containerRef}>
    {children}
  </div>
);

const DialogFocusTrapContainer = forwardRef(DialogFocusTrapContainerComponent);

/**
 * Simple wrapper around CMSDS Dialog that only renders `children` when open
 */
export const MctDialog: FCWithChildren<DialogProps & MctDialogProps> = ({
  actionsClassName,
  children,
  className,
  headerClassName,
  isOpen,
  hasFocusTrap = true,
  responsiveActions,
  ...props
}) => {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const focusTrapIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const focusTrapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const isMountedRef = useRef(true);
  const uniqueId = useId();
  const renderFocusTrapComponent = !TESTING && hasFocusTrap;
  const WrapperEl = renderFocusTrapComponent ? FocusTrap : Fragment;
  const DialogWrapperEl = renderFocusTrapComponent
    ? DialogFocusTrapContainer
    : Fragment;
  const dialogClassName = "mct-c-dialog";
  const safeClearInterval = useCallback(() => {
    if (focusTrapIntervalRef.current) {
      clearInterval(focusTrapIntervalRef.current);
      focusTrapIntervalRef.current = null;
    }
  }, []);
  const safeClearTimeout = useCallback(() => {
    if (focusTrapTimeoutRef.current) {
      clearTimeout(focusTrapTimeoutRef.current);
      focusTrapTimeoutRef.current = null;
    }
  }, []);
  const focusTrapOptions: FocusTrapProps["focusTrapOptions"] = useMemo(
    () => ({
      checkCanFocusTrap: trapContainers =>
        new Promise(resolve => {
          const container = trapContainers[0]; // There is only one container here
          let resolved = false;
          // Always clear any previous interval/timeout before starting new ones
          safeClearInterval();
          safeClearTimeout();
          focusTrapIntervalRef.current = setInterval(() => {
            if (
              tabbable(container).length > 0 ||
              isMountedRef.current === false
            ) {
              safeClearInterval();
              safeClearTimeout();
              if (!resolved) {
                resolved = true;
                resolve();
              }
            }
          }, FOCUS_TRAP_CHECK_INTERVAL);
          // fallback in case no tabbable element is found
          focusTrapTimeoutRef.current = setTimeout(() => {
            safeClearInterval();
            safeClearTimeout();
            if (resolved || isMountedRef.current === false) {
              return;
            }
            resolved = true;
            resolve();
          }, FOCUS_TRAP_TIMEOUT_FALLBACK);
        }),
      onActivate: () => {
        devDebug("🪤 Focus trap activated");
        dialogRef.current?.classList.add("focus-trap-active");
      },
      onDeactivate: () => {
        devDebug("🪤 Focus trap being deactivated");
        dialogRef.current?.classList.remove("focus-trap-active");
      },
      // Called after focus is sent to the focus trap
      onPostActivate: () => {
        devDebug("🪤 Focus has been sent to the animated focus trap");
      },
      returnFocusOnDeactivate: true,
    }),
    [safeClearInterval, safeClearTimeout]
  );

  useEffect(() => {
    if (!dialogRef.current) {
      dialogRef.current = document.querySelector(
        `[data-id="${uniqueId}"]`
      ) as HTMLDialogElement | null;
    }
  }, [uniqueId]);

  useEffect(
    () => () => {
      isMountedRef.current = false;
      safeClearInterval();
      safeClearTimeout();
    },
    [safeClearInterval, safeClearTimeout]
  );

  return (
    <PortalWrapper renderToPortal={renderFocusTrapComponent}>
      <WrapperEl
        {...(renderFocusTrapComponent
          ? { active: isOpen, focusTrapOptions }
          : {})}
      >
        <DialogWrapperEl>
          <Dialog
            actionsClassName={classNames(
              `${dialogClassName}__actions`,
              actionsClassName,
              {
                [`${dialogClassName}__actions--responsive`]: responsiveActions,
              }
            )}
            className={classNames(className, dialogClassName)}
            headerClassName={classNames(
              `${dialogClassName}__header`,
              headerClassName
            )}
            isOpen={isOpen}
            data-id={uniqueId}
            {...props}
          >
            {isOpen && children}
          </Dialog>
        </DialogWrapperEl>
      </WrapperEl>
    </PortalWrapper>
  );
};
